import { RuntimeHostSettings } from 'environments/environment.types';

export const AUTH_STORAGE_KEYS = {
    accessToken: 'access_token',
    accessTokenTimestamp: 'access_token_timestamp',
    impersonatorAccessToken: 'impersonator_access_token',

    hostApp: 'app',
    hostSupport: 'support',
    hostTheme: 'theme',
    hostLogo: 'logo',
    hostWatermark: 'watermark',
    hostFavicon: 'favicon',

    user: 'user',
    organizations: 'organizations',
    access: 'access',
    selectedOrganization: 'o-s',
    selectedBranch: 'b-s',
    impersonatorSelectedOrganization: 'o-i-s',
    impersonatorSelectedBranch: 'b-i-s'
} as const;

export type AuthStorageKey = (typeof AUTH_STORAGE_KEYS)[keyof typeof AUTH_STORAGE_KEYS];

const APP_SETTINGS_STORAGE_KEY = 'app-settings';
const APP_USER_STORAGE_KEY = 'app-user';
const APP_SELECTION_STORAGE_KEY = 'app-selection';

type RuntimeStorageBucket = 'settings' | 'user' | 'selection';

type HostWindowSettings = RuntimeHostSettings & {
    organization?: unknown;
    branch?: unknown;
};

type StorageFieldDescriptor = {
    bucket: RuntimeStorageBucket;
    path: string;
    json: boolean;
};

const STORAGE_KEY_MAP: Partial<Record<AuthStorageKey, StorageFieldDescriptor>> = {
    [AUTH_STORAGE_KEYS.hostApp]: { bucket: 'settings', path: 'app', json: true },
    [AUTH_STORAGE_KEYS.hostSupport]: { bucket: 'settings', path: 'support', json: true },
    [AUTH_STORAGE_KEYS.hostTheme]: { bucket: 'settings', path: 'layout.theme', json: false },
    [AUTH_STORAGE_KEYS.hostLogo]: { bucket: 'settings', path: 'layout.logo', json: false },
    [AUTH_STORAGE_KEYS.hostWatermark]: { bucket: 'settings', path: 'layout.watermark', json: false },
    [AUTH_STORAGE_KEYS.hostFavicon]: { bucket: 'settings', path: 'layout.favicon', json: false },
    [AUTH_STORAGE_KEYS.user]: { bucket: 'user', path: 'user', json: true },
    [AUTH_STORAGE_KEYS.organizations]: { bucket: 'user', path: 'organizations', json: true },
    [AUTH_STORAGE_KEYS.access]: { bucket: 'user', path: 'access', json: true },
    [AUTH_STORAGE_KEYS.selectedOrganization]: { bucket: 'selection', path: 'organization', json: true },
    [AUTH_STORAGE_KEYS.selectedBranch]: { bucket: 'selection', path: 'branch', json: true }
};

const SETTINGS_BUCKET_KEYS = ['url', 'app', 'layout', 'support'] as const;
const USER_BUCKET_KEYS = ['user', 'roles', 'access', 'organizations'] as const;
const SELECTION_BUCKET_KEYS = ['organization', 'branch', 'selection'] as const;

export const AUTH_SESSION_RESET_KEYS: readonly AuthStorageKey[] = [
    AUTH_STORAGE_KEYS.accessToken,
    AUTH_STORAGE_KEYS.accessTokenTimestamp,
    AUTH_STORAGE_KEYS.user,
    AUTH_STORAGE_KEYS.impersonatorAccessToken,
    AUTH_STORAGE_KEYS.selectedOrganization,
    AUTH_STORAGE_KEYS.selectedBranch
] as const;

export const AUTH_REFRESH_FAILURE_RESET_KEYS: readonly AuthStorageKey[] = [
    AUTH_STORAGE_KEYS.accessToken,
    AUTH_STORAGE_KEYS.accessTokenTimestamp,
    AUTH_STORAGE_KEYS.user,
    AUTH_STORAGE_KEYS.impersonatorAccessToken
] as const;

export const AUTH_IMPERSONATION_SELECTION_KEYS: readonly AuthStorageKey[] = [
    AUTH_STORAGE_KEYS.impersonatorSelectedOrganization,
    AUTH_STORAGE_KEYS.impersonatorSelectedBranch
] as const;

function hasStorage(): boolean {
    return typeof localStorage !== 'undefined';
}

function getBrowserWindow(): (Window & { settings?: HostWindowSettings; __HOST_SETTINGS__?: HostWindowSettings }) | null {
    if (typeof window === 'undefined') {
        return null;
    }

    return window as Window & { settings?: HostWindowSettings; __HOST_SETTINGS__?: HostWindowSettings };
}

function readStorageObject(storageKey: string): Record<string, any> | null {
    if (!hasStorage()) {
        return null;
    }

    const raw = localStorage.getItem(storageKey);
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed as Record<string, any> : null;
    } catch {
        localStorage.removeItem(storageKey);
        return null;
    }
}

function writeStorageObject(storageKey: string, value: Record<string, any>): void {
    if (!hasStorage()) {
        return;
    }

    if (!value || Object.keys(value).length === 0) {
        localStorage.removeItem(storageKey);
        return;
    }

    try {
        localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
        // Ignore storage serialization errors.
    }
}

function pickDefinedKeys<T extends Record<string, any>>(source: T, keys: readonly string[]): Record<string, any> {
    return keys.reduce((accumulator: Record<string, any>, key: string) => {
        const value = source[key];
        if (value !== undefined && value !== null) {
            accumulator[key] = value;
        }
        return accumulator;
    }, {});
}

function readRuntimeSettingsFromStorage(): HostWindowSettings {
    return {
        ...(readStorageObject(APP_SETTINGS_STORAGE_KEY) || {}),
        ...(readStorageObject(APP_USER_STORAGE_KEY) || {}),
        ...(readStorageObject(APP_SELECTION_STORAGE_KEY) || {})
    } as HostWindowSettings;
}

function writeRuntimeSettingsToStorage(settings: HostWindowSettings): void {
    writeStorageObject(APP_SETTINGS_STORAGE_KEY, pickDefinedKeys(settings as Record<string, any>, SETTINGS_BUCKET_KEYS));
    writeStorageObject(APP_USER_STORAGE_KEY, pickDefinedKeys(settings as Record<string, any>, USER_BUCKET_KEYS));
    writeStorageObject(APP_SELECTION_STORAGE_KEY, pickDefinedKeys(settings as Record<string, any>, SELECTION_BUCKET_KEYS));
}

function getWindowSettings(): HostWindowSettings | null {
    const browserWindow = getBrowserWindow();
    const storedSettings = readRuntimeSettingsFromStorage();

    if (browserWindow) {
        const legacySettings = browserWindow.settings && typeof browserWindow.settings === 'object'
            ? browserWindow.settings
            : {};

        const runtimeSettings: HostWindowSettings = {
            ...storedSettings,
            ...legacySettings,
            url: browserWindow.url ?? legacySettings.url ?? storedSettings.url,
            layout: browserWindow.layout ?? legacySettings.layout ?? storedSettings.layout,
            support: browserWindow.support ?? legacySettings.support ?? storedSettings.support,
            app: browserWindow.app ?? legacySettings.app ?? storedSettings.app,
            organizations: browserWindow.organizations ?? legacySettings.organizations ?? storedSettings.organizations,
            user: browserWindow.user ?? legacySettings.user ?? storedSettings.user,
            access: browserWindow.access ?? legacySettings.access ?? storedSettings.access,
            roles: browserWindow.roles ?? legacySettings.roles ?? storedSettings.roles,
            selection: browserWindow.selection ?? legacySettings.selection ?? storedSettings.selection,
            organization: (browserWindow as any).organization ?? legacySettings.organization ?? storedSettings.organization,
            branch: (browserWindow as any).branch ?? legacySettings.branch ?? storedSettings.branch
        };

        if (Object.keys(runtimeSettings).length > 0) {
            return runtimeSettings;
        }

        const legacyHostSettings = browserWindow.__HOST_SETTINGS__;
        if (legacyHostSettings && typeof legacyHostSettings === 'object') {
            return legacyHostSettings;
        }
    }

    return storedSettings;
}

function setWindowSettings(settings: HostWindowSettings): void {
    const browserWindow = getBrowserWindow();
    if (!browserWindow) {
        return;
    }

    browserWindow.url = settings.url;
    browserWindow.layout = settings.layout;
    browserWindow.support = settings.support;
    browserWindow.app = settings.app;
    browserWindow.organizations = settings.organizations;
    browserWindow.user = settings.user;
    browserWindow.access = settings.access;
    browserWindow.roles = settings.roles;
    browserWindow.selection = settings.selection;
    (browserWindow as any).organization = settings.organization;
    (browserWindow as any).branch = settings.branch;

    browserWindow.settings = settings;
    browserWindow.__HOST_SETTINGS__ = settings;
}

function isManagedRuntimeKey(key: AuthStorageKey): boolean {
    return !!STORAGE_KEY_MAP[key];
}

function getStorageFieldDescriptor(key: AuthStorageKey): StorageFieldDescriptor | null {
    return STORAGE_KEY_MAP[key] ?? null;
}

function getValueAtPath(source: any, path: string): unknown {
    if (!source || !path) {
        return null;
    }

    const segments = path.split('.');
    let cursor: any = source;

    for (const segment of segments) {
        if (!cursor || typeof cursor !== 'object' || !(segment in cursor)) {
            return null;
        }

        cursor = cursor[segment];
    }

    return cursor ?? null;
}

function setValueAtPath(target: any, path: string, value: unknown): void {
    const segments = path.split('.');
    let cursor = target;

    for (let i = 0; i < segments.length - 1; i += 1) {
        const segment = segments[i];
        if (!cursor[segment] || typeof cursor[segment] !== 'object') {
            cursor[segment] = {};
        }
        cursor = cursor[segment];
    }

    cursor[segments[segments.length - 1]] = value;
}

function removeValueAtPath(target: any, path: string): void {
    const segments = path.split('.');
    let cursor = target;

    for (let i = 0; i < segments.length - 1; i += 1) {
        const segment = segments[i];
        if (!cursor[segment] || typeof cursor[segment] !== 'object') {
            return;
        }
        cursor = cursor[segment];
    }

    delete cursor[segments[segments.length - 1]];
}

function getRuntimeSettings(): HostWindowSettings {
    return { ...(getWindowSettings() || {}) };
}

function persistRuntimeSettings(settings: HostWindowSettings): void {
    setWindowSettings(settings);
    writeRuntimeSettingsToStorage(settings);
}

function getManagedStringValue(key: AuthStorageKey): string {
    const descriptor = getStorageFieldDescriptor(key);
    const path = descriptor?.path || '';
    if (!path) {
        return '';
    }

    const settings = getRuntimeSettings();
    if (!settings) {
        return '';
    }

    const value = getValueAtPath(settings, path);

    if (value == null) {
        return '';
    }

    if (typeof value === 'string') {
        return value;
    }

    try {
        return JSON.stringify(value);
    } catch {
        return '';
    }
}

function setManagedRuntimeValue(key: AuthStorageKey, value: unknown): void {
    const descriptor = getStorageFieldDescriptor(key);
    const path = descriptor?.path || '';
    if (!path) {
        return;
    }

    const settings = getRuntimeSettings();

    if (value === '' || value == null) {
        removeValueAtPath(settings, path);
    } else {
        setValueAtPath(settings, path, value);
    }

    persistRuntimeSettings(settings);
}

function parseJsonSafe<T>(raw: string): T | null {
    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
}

function getManagedRawValue(key: AuthStorageKey): unknown {
    const descriptor = getStorageFieldDescriptor(key);
    const path = descriptor?.path || '';
    if (!path) {
        return null;
    }

    return getValueAtPath(getRuntimeSettings(), path);
}

function isManagedJsonKey(key: AuthStorageKey): boolean {
    return getStorageFieldDescriptor(key)?.json === true;
}

export const AuthStorage = {
    get(key: AuthStorageKey): string {
        if (isManagedRuntimeKey(key)) {
            return getManagedStringValue(key);
        }

        if (!hasStorage()) {
            return '';
        }

        return localStorage.getItem(key) ?? '';
    },

    set(key: AuthStorageKey, value: string): void {
        if (isManagedRuntimeKey(key)) {
            if (isManagedJsonKey(key)) {
                const parsed = parseJsonSafe<unknown>(value);
                setManagedRuntimeValue(key, parsed ?? value);
            } else {
                setManagedRuntimeValue(key, value);
            }
            return;
        }

        if (!hasStorage()) {
            return;
        }

        localStorage.setItem(key, value);
    },

    remove(key: AuthStorageKey): void {
        if (isManagedRuntimeKey(key)) {
            setManagedRuntimeValue(key, null);
            return;
        }

        if (!hasStorage()) {
            return;
        }

        localStorage.removeItem(key);
    },

    removeMany(keys: readonly AuthStorageKey[]): void {
        const canUseStorage = hasStorage();

        keys.forEach((key) => {
            if (isManagedRuntimeKey(key)) {
                setManagedRuntimeValue(key, null);
                return;
            }

            if (canUseStorage) {
                localStorage.removeItem(key);
            }
        });
    },

    setJson(key: AuthStorageKey, value: unknown): void {
        if (isManagedRuntimeKey(key)) {
            setManagedRuntimeValue(key, value);
            return;
        }

        if (!hasStorage()) {
            return;
        }

        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch {
            // Ignore storage serialization errors.
        }
    },

    getJson<T>(key: AuthStorageKey): T | null {
        if (isManagedRuntimeKey(key)) {
            const value = getManagedRawValue(key);
            if (value == null) {
                return null;
            }

            if (typeof value === 'string') {
                const parsed = parseJsonSafe<T>(value);
                return parsed ?? (value as unknown as T);
            }

            return value as T;
        }

        if (!hasStorage()) {
            return null;
        }

        const raw = localStorage.getItem(key);
        if (!raw) {
            return null;
        }

        try {
            return JSON.parse(raw) as T;
        } catch {
            localStorage.removeItem(key);
            return null;
        }
    }
} as const;

export function getRuntimeApiUrl(fallbackUrl: string = ''): string {
    const settings = getRuntimeSettings();
    const configuredUrl = typeof settings.url?.api === 'string'
        ? settings.url.api.trim()
        : '';

    return configuredUrl || fallbackUrl;
}

export function persistRuntimeEnvironment(environmentState: {
    url?: unknown;
    app?: unknown;
    user?: unknown;
    roles?: unknown;
    access?: unknown;
    selection?: unknown;
    organizations?: unknown;
}): void {
    const settings = getRuntimeSettings() as HostWindowSettings & Record<string, unknown>;

    if (environmentState.url !== undefined) {
        settings.url = environmentState.url as any;
    }

    if (environmentState.app !== undefined) {
        settings.app = environmentState.app as any;
    }

    if (environmentState.user !== undefined) {
        settings.user = environmentState.user as any;
    }

    if (environmentState.roles !== undefined) {
        settings.roles = environmentState.roles as any;
    }

    if (environmentState.access !== undefined) {
        settings.access = environmentState.access as any;
    }

    if (environmentState.selection !== undefined) {
        settings.selection = environmentState.selection as any;
    }

    if (environmentState.organizations !== undefined) {
        settings.organizations = environmentState.organizations as any;
    }

    persistRuntimeSettings(settings);
}

export function clearRuntimeEnvironment(): void {
    const settings = getRuntimeSettings() as HostWindowSettings & Record<string, unknown>;

    delete settings.user;
    delete settings.organization;
    delete settings.branch;
    delete settings.roles;
    delete settings.access;
    delete settings.selection;
    delete settings.organizations;

    persistRuntimeSettings(settings);
}
